import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Outlet, useLocation } from "react-router-dom";
import { canAccessSidebarPath } from "../../Layouts/Sidebar/sidebarAccess";
import {
  isManager,
  isSeller,
} from "../../lib/utility";
import {
  authSelector,
  getNavOptionsData,
  loadAbacSessionPermissions,
} from "../Auth/AuthSlice";
import {
  hasNoAllowedAbacPages,
  isAbacRouteAllowed,
} from "../User/Abac/abacRouteMap";
import {
  isMultiAccessUser,
  shouldEnforceAbacForUser,
} from "../User/Abac/abacAccess";
import { middleware } from "../../redux/store";
import NoAccessPage from "./NoAccessPage";
import RedirectToDashboard from "./RedirectToDashboard";
import SuspenseLoader from "./SuspenseLoader";


const NavAccessGuard = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const {
    user,
    navOptions,
    abacPermissions,
    abacSessionLoaded,
    isFetchingAbacSession,
    isFetchingNavOptions,
    isnavOptionsFetchSuccess,
    isnavOptionsFetchError,
  } = useSelector(authSelector);
  const profileData = globalThis.profileData || {};

  const needsNavOptions = !isSeller(user) && !isManager(user);
  const navOptionsSettled =
    !needsNavOptions || isnavOptionsFetchSuccess || isnavOptionsFetchError;

  const needsAbac =
    isMultiAccessUser(user, profileData) &&
    shouldEnforceAbacForUser(user, profileData);
  const abacSettled = !needsAbac || abacSessionLoaded || !isFetchingAbacSession;

  useEffect(() => {
    if (
      needsNavOptions &&
      !isFetchingNavOptions &&
      !isnavOptionsFetchSuccess &&
      !isnavOptionsFetchError
    ) {
      dispatch(getNavOptionsData()).then((action) =>
        middleware(action, "Nav Options")
      );
    }
  }, [
    dispatch,
    needsNavOptions,
    isFetchingNavOptions,
    isnavOptionsFetchSuccess,
    isnavOptionsFetchError,
  ]);

  useEffect(() => {
    if (
      needsAbac &&
      !abacSessionLoaded &&
      !isFetchingAbacSession &&
      user?.accessToken
    ) {
      dispatch(loadAbacSessionPermissions());
    }
  }, [
    dispatch,
    needsAbac,
    abacSessionLoaded,
    isFetchingAbacSession,
    user?.accessToken,
  ]);

  // MA permissions loading — neutral wait (No Access only after settled + all off).
  if (needsAbac && (!abacSettled || isFetchingAbacSession)) {
    return (
      <SuspenseLoader>
        <></>
      </SuspenseLoader>
    );
  }

  if (needsNavOptions && !navOptionsSettled) {
    return (
      <SuspenseLoader>
        <></>
      </SuspenseLoader>
    );
  }

  // All toggles off → No Access (only once ABAC has settled).
  if (
    needsAbac &&
    hasNoAllowedAbacPages(abacPermissions, user, profileData)
  ) {
    return <NoAccessPage />;
  }

  const allowed = canAccessSidebarPath(
    location.pathname,
    user,
    navOptions || {}
  );

  if (!allowed) {
    return <RedirectToDashboard />;
  }

  if (
    !isAbacRouteAllowed(
      location.pathname,
      abacPermissions,
      user,
      profileData
    )
  ) {
    return <RedirectToDashboard />;
  }

  return (
    <SuspenseLoader>
      <Outlet />
    </SuspenseLoader>
  );
};

export default NavAccessGuard;
